import { inLogistics, type OrderState } from './orderState';
import { shippingBadge, type Shipping, type ShippingBadgeKey } from './shipping';
import type { Tone } from '../tone';

/**
 * The one status a case is read as, anywhere a case appears in a list.
 *
 * It was already being computed, in the Orders table's Status cell: a case past
 * Ready shows where its parcel is, and a case still being made shows where the
 * order stands. What did not exist was a NAME for that decision, so the Status
 * filter beside the column offered four order states and none of the shipping
 * ones. A reader could see a row saying Shipped and had no way to ask for every
 * row saying Shipped, which is what the client reported.
 *
 * One function now, and the filter's options are exactly the values it can
 * return, so the two cannot drift.
 */

export type CaseStatusKey =
  | Exclude<OrderState, 'dispatched'>
  | Exclude<ShippingBadgeKey, 'notYet' | 'onHold' | 'pendingDispatch'>;

/**
 * In the order a case lives through them, which is the order the select offers.
 *
 * `onHold` and `pendingDispatch` are not here because they are the same fact as
 * the order states `held` and `paid`, worded twice: a held case reads On hold
 * whether the hold came before Ready or after it, and a paid case is pending
 * dispatch. One entry each, or the select asks the reader to tell two identical
 * options apart.
 *
 * A paid case here is one that is paid and finished. One paid before its work
 * is done has the state `production`, and reads as a case being made does: In
 * production, Due today, Overdue. The request of 8 October 2026 (third).
 */
export const CASE_STATUS_KEYS: readonly CaseStatusKey[] = [
  'production',
  'ready_deliver',
  'held',
  'paid',
  'awaitingWaybill',
  'shipped',
  'outForDelivery',
  'received',
];

/** What a row carries, whichever screen it is on. */
export interface StatusableCase {
  state: OrderState;
  dispatch: Pick<Shipping, 'status' | 'method' | 'deliveredAt'>;
  /** Null once the order is dispatched, which is what says every tray is done. */
  currentStage: string | null;
}

/**
 * The status one case reads as.
 *
 * A case that has moved past Ready is read from its parcel, because "where is
 * it" is the only question anybody asks about a case that has left the bench.
 * Everything else is read from the order.
 */
export function caseStatus(order: StatusableCase): CaseStatusKey {
  const moving = inLogistics({
    state: order.state,
    kitsAllReady: order.currentStage === null || order.currentStage === 'ready',
    delivered: order.dispatch.deliveredAt !== null,
  });

  // `notYet` is the badge's own word for "nothing to say about shipping", which
  // is exactly the case that has not moved, so the two paths meet in one switch
  // rather than in two returns that have to agree.
  const badge = moving ? shippingBadge(order).key : 'notYet';

  switch (badge) {
    // The two the badge and the order state both name, answered once each.
    case 'onHold':
      return 'held';
    case 'pendingDispatch':
      return 'paid';
    /*
     * Nothing to say about the parcel: read the order instead. A DELIVERED case
     * arrives here, because it is finished business and so in neither Ready nor
     * Logistics, and Received is what the reader is looking for.
     */
    case 'notYet':
      return order.state === 'dispatched' ? 'received' : order.state;
    default:
      return badge;
  }
}

/**
 * The tone each status is drawn in, beside the rule that chooses it and not in
 * the words module: `ORDER_STATE_LABEL` already keeps its tones here, and a
 * status whose colour lived somewhere else would be a second place to forget.
 */
export const CASE_STATUS_TONE: Record<CaseStatusKey, Tone> = {
  production: 'watch',
  ready_deliver: 'watch',
  held: 'hold',
  paid: 'ok',
  awaitingWaybill: 'watch',
  shipped: 'ok',
  outForDelivery: 'ok',
  received: 'done',
};

/**
 * The statuses only a case that has LEFT the lab can have.
 *
 * A dispatched order leaves the active list for the archive, which is right for
 * a list of work in hand and wrong the moment somebody asks it for every case
 * that is shipped. A screen offering these asks the server for the dispatched
 * ones too, and only while one of them is chosen, so the list it shows by
 * default is the list it showed before.
 */
export function needsDispatched(status: CaseStatusKey): boolean {
  return status === 'awaitingWaybill'
    || status === 'shipped'
    || status === 'outForDelivery'
    || status === 'received';
}
